import React, { useRef, useCallback, useState } from 'react';
import {
  Button,
  Box,
  Card,
  CardActions,
  CardActionArea,
  CardContent,
  Typography,
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import AddIcon from '@mui/icons-material/Add';
import BillingAddressForm from './BillingAddressForm';
import { BillingAddress, PartialBy } from '../../redux/types';
import ApiClient from '../../loopback/apiClient';

const CardContainer = styled(Box)`
  width: 100%;
  height: 140px;
  position: relative;
`;

type Props = {
  add?: boolean;
  userId?: number;
  billingAddress?: BillingAddress;
  addBillingAddress: (a: BillingAddress) => void;
  updateBillingAddress: (a: BillingAddress) => void;
  deleteBillingAddress: (a: BillingAddress) => void;
  setEditId: React.Dispatch<React.SetStateAction<number | undefined>>;
  editMode?: boolean;
  onSelect?: (id: number) => void;
}

const BillingAddressCard: React.FC<Props> = ({
  add,
  userId,
  billingAddress,
  addBillingAddress,
  updateBillingAddress,
  deleteBillingAddress,
  setEditId,
  editMode,
  onSelect,
}: Props) => {
  const submitBtnRef = useRef<HTMLButtonElement|null>(null);

  const [promptDelete, setPromptDelete] = useState<boolean>(false);

  const onAddClick = () => {
    if (add) {
      setEditId(-1);
    }
  };

  const onSelectClick = () => {
    if (onSelect && billingAddress) {
      onSelect(billingAddress.id);
    }
  };

  const onEditClick = () => {
    if (billingAddress) {
      setEditId(billingAddress.id);
    }
  };

  const onCancelClick = () => {
    setEditId(undefined);
  };

  const onSaveClick = () => {
    submitBtnRef.current?.click();
  };

  const onDeleteClick = () => {
    setPromptDelete(true);
  };

  const onDeleteConfirmCancel = () => {
    setPromptDelete(false);
  };

  const onDeleteConfirm = () => {
    if (billingAddress) {
      ApiClient.deleteBillingAddress(billingAddress.id)
        .then(() => {
          deleteBillingAddress(billingAddress);
          setPromptDelete(false);
        });
    }
  };

  const onSubmitClick = useCallback((data?: PartialBy<Omit<BillingAddress, 'quipUserId'>, 'id'>) => {
    if (data && add && userId) {
      ApiClient.addBillingAddress({ ...data, quipUserId: userId })
        .then((r) => {
          addBillingAddress(r);
          setEditId(undefined);
        });
    } else if (billingAddress !== undefined) {
      // edit
      ApiClient.addBillingAddress({
        ...data,
        quipUserId: billingAddress.quipUserId,
        id: billingAddress.id,
      } as BillingAddress)
        .then((r) => {
          updateBillingAddress(r);
          setEditId(undefined);
        });
    }
  }, [add, userId, billingAddress, addBillingAddress, updateBillingAddress, setEditId]);

  return (
    <Card
      sx={{
        display: 'block',
        height: editMode ? '100%' : 214,
        transition: (theme: Theme) => theme.transitions.create(['width', 'height'], {
          duration: theme.transitions.duration.short,
        }),
      }}
    >
      {
        !editMode && (
          <>
            {
              add && !promptDelete && (
                <CardActionArea onClick={onAddClick}>
                  <CardContent>
                    <CardContainer>
                      <AddIcon
                        sx={{
                          position: 'absolute',
                          top: '40%',
                          right: '45%',
                        }}
                      />
                      <Typography
                        variant="overline"
                        sx={{
                          position: 'absolute',
                          top: '60%',
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        <FormattedMessage id="billingAddress.add" />
                      </Typography>
                    </CardContainer>
                  </CardContent>
                </CardActionArea>
              )
            }
            {
              !add && billingAddress && !promptDelete && (
                <CardContent>
                  <CardContainer>
                    <Typography gutterBottom variant="body1" noWrap>{billingAddress.name}</Typography>
                    <Typography variant="body2" noWrap>{`${billingAddress.street} ${billingAddress.houseNumber}`}</Typography>
                    <Typography variant="body2" noWrap>{`${billingAddress.postcode} ${billingAddress.city}`}</Typography>
                    <Typography gutterBottom variant="body2" noWrap>{`${billingAddress.country}`}</Typography>
                    <Typography variant="body2" noWrap>{billingAddress.email}</Typography>
                    <Typography variant="body2" noWrap>{billingAddress.contactNumber}</Typography>
                  </CardContainer>
                </CardContent>
              )
            }
            {
              promptDelete && (
                <CardContent>
                  <CardContainer display="flex">
                    <Typography><FormattedMessage id="billingAddress.deletePrompt" /></Typography>
                  </CardContainer>
                </CardContent>
              )
            }
          </>
        )
      }
      {
        editMode && !promptDelete && (
          <CardContent>
            <BillingAddressForm
              submitButtonRef={submitBtnRef}
              onSubmit={onSubmitClick}
              defaultData={billingAddress}
            />
          </CardContent>
        )
      }
      <CardActions>
        {
          !add && !editMode && !promptDelete && (
            <>
              {
                onSelect && (
                  <>
                    <Button size="small" onClick={onSelectClick} color="primary"><FormattedMessage id="trialSetup.chooseInstitute" /></Button>
                    |
                  </>
                )
              }
              <Button size="small" onClick={onEditClick} color="primary"><FormattedMessage id="edit" /></Button>
              |
              <Button size="small" onClick={onDeleteClick} color="secondary"><FormattedMessage id="genericDelete" /></Button>
            </>
          )
        }
        {
          editMode && !promptDelete && (
            <>
              <Button onClick={onSaveClick} size="small" color="primary"><FormattedMessage id="save" /></Button>
              <Button onClick={onCancelClick} size="small" color="secondary"><FormattedMessage id="genericCancel" /></Button>
            </>
          )
        }
        {
          promptDelete && (
            <>
              <Button size="small" color="primary" onClick={onDeleteConfirm}><FormattedMessage id="confirm" /></Button>
              <Button size="small" color="secondary" onClick={onDeleteConfirmCancel}><FormattedMessage id="genericCancel" /></Button>
            </>
          )
        }
      </CardActions>
    </Card>
  );
};

export default BillingAddressCard;
